import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const LEVELS = [4, 3, 2, 1];
const ROWS = ["A", "B"];
const POS = [1, 2, 3, 4];
const locId = (code, l, r, p) => `${code}-L${l}-${r}-P${p}`;

const fmt = (n) => {
  if (n == null || isNaN(n)) return "0";
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? r.toLocaleString() : r.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

const fgBreakdown = (cases, cpp, casePack) => {
  const parts = [];
  if (cpp && cases >= cpp) parts.push(`${fmt(cases / cpp)} Pallet${cases / cpp === 1 ? "" : "s"}`);
  parts.push(`${fmt(cases)} Cases`);
  if (casePack) parts.push(`${fmt(cases * casePack)} Units`);
  return parts;
};

const dot = (...parts) => parts.filter(Boolean).join(" · ");
const fgLabel = (fg) => dot(fg.product_name, fg.design_name, fg.case_pack ? `${fg.case_pack}-Pack` : "");

export default function FGShelfView() {
  const [fgs, setFgs] = useState([]);
  const [racks, setRacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renaming, setRenaming] = useState(null);
  const [assignAt, setAssignAt] = useState(null);
  const [detailAt, setDetailAt] = useState(null);
  const [assignFgId, setAssignFgId] = useState("");
  const [assignQty, setAssignQty] = useState("");
  const [assignUnit, setAssignUnit] = useState("pallets");
  const [reconciling, setReconciling] = useState(false);

  const load = async () => {
    try {
      const [f, r] = await Promise.all([api.get("/finished-goods"), api.get("/fg-racks")]);
      setFgs(f.data);
      setRacks(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const assignedByFg = useMemo(() => {
    const m = {};
    racks.forEach((r) => Object.values(r.slots || {}).forEach((s) => {
      m[s.fg_id] = (m[s.fg_id] || 0) + Number(s.cases || 0);
    }));
    return m;
  }, [racks]);

  const unassignedList = useMemo(() => fgs
    .map((f) => ({ ...f, assigned: assignedByFg[f.id] || 0 }))
    .map((f) => ({ ...f, unassigned: (f.qty_on_hand || 0) - f.assigned }))
    .filter((f) => f.unassigned > 0.0001), [fgs, assignedByFg]);

  const excessList = useMemo(() => fgs
    .map((f) => ({ ...f, assigned: assignedByFg[f.id] || 0 }))
    .filter((f) => f.assigned > 0.0001 && f.assigned - Math.max(0, f.qty_on_hand || 0) > 0.0001), [fgs, assignedByFg]);

  const orphanSlots = useMemo(() => {
    const knownFgIds = new Set(fgs.map((f) => f.id));
    return racks.flatMap((r) => Object.entries(r.slots || {})
      .filter(([, s]) => !knownFgIds.has(s.fg_id))
      .map(([loc, s]) => ({ loc, name: dot(s.sku, s.product_name, s.design_name) || "Unknown item", cases: Number(s.cases || 0) })));
  }, [fgs, racks]);
  const needsReconcile = excessList.length > 0 || orphanSlots.length > 0;

  const addRack = async () => {
    try {
      const { data } = await api.post("/fg-racks", {});
      setRacks((p) => [...p, data]);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const deleteRack = async (rack) => {
    if (!window.confirm(`Delete ${rack.name}? Any finished goods assigned here return to Unassigned.`)) return;
    try {
      await api.delete(`/fg-racks/${rack.id}`);
      setRacks((p) => p.filter((r) => r.id !== rack.id));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const saveRename = async () => {
    if (!renaming?.name.trim()) return;
    try {
      const { data } = await api.put(`/fg-racks/${renaming.id}`, { name: renaming.name.trim() });
      setRacks((p) => p.map((r) => (r.id === data.id ? data : r)));
      setRenaming(null);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const pickFg = (id) => {
    setAssignFgId(id);
    const fg = fgs.find((f) => f.id === id);
    if (!fg?.cases_per_pallet) setAssignUnit("cases");
  };

  const openAssign = (target) => {
    const pre = target?.preselect || "";
    setAssignFgId(pre);
    setAssignQty("");
    const preFg = fgs.find((f) => f.id === pre);
    setAssignUnit(pre && !preFg?.cases_per_pallet ? "cases" : "pallets");
    setAssignAt(target);
  };

  const submitAssign = async () => {
    if (!assignAt?.rackId) { toast.error("Pick an open slot on a rack below first."); return; }
    if (!assignFgId || !Number(assignQty)) { toast.error("Pick a finished good and quantity."); return; }
    try {
      const { data } = await api.put(`/fg-racks/${assignAt.rackId}/slots/${assignAt.location}`, {
        fg_id: assignFgId, qty: Number(assignQty), unit: assignUnit,
      });
      setRacks((p) => p.map((r) => (r.id === data.id ? data : r)));
      setAssignAt(null);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const autoAssign = async (fgId) => {
    try {
      const { data } = await api.post("/fg-racks/auto-assign", { fg_id: fgId });
      if (data.assigned_slots === 0) toast.info("Nothing to assign.");
      else toast.success(`Auto-assigned ${fmt(data.assigned_cases)} cases across ${data.assigned_slots} slot${data.assigned_slots === 1 ? "" : "s"}${data.remaining_cases > 0 ? ` — ${fmt(data.remaining_cases)} cases left (no empty slots)` : ""}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const clearSlot = async (rackId, location) => {
    try {
      const { data } = await api.delete(`/fg-racks/${rackId}/slots/${location}`);
      setRacks((p) => p.map((r) => (r.id === data.id ? data : r)));
      setDetailAt(null);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const reconcileNow = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/fg-racks/reconcile");
      const parts = [];
      if (data.removed_orphan_slots > 0) parts.push(`${data.removed_orphan_slots} orphaned slot${data.removed_orphan_slots === 1 ? "" : "s"} removed`);
      (data.trimmed || []).forEach((t) => parts.push(`${t.name}: trimmed ${t.cases} cases`));
      toast.success(parts.length ? `Reconciled — ${parts.join("; ")}` : "Nothing to reconcile");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    setReconciling(false);
  };

  if (loading) return <div className="flex justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const detailSlot = detailAt ? (racks.find((r) => r.id === detailAt.rackId)?.slots || {})[detailAt.location] : null;
  const selectedFg = fgs.find((f) => f.id === assignFgId);
  const selectedUnassigned = selectedFg ? (selectedFg.qty_on_hand || 0) - (assignedByFg[selectedFg.id] || 0) : null;

  const renderSlot = (rack, loc) => {
    const slot = (rack.slots || {})[loc];
    return (
      <button key={loc}
        onClick={() => slot ? setDetailAt({ rackId: rack.id, location: loc }) : openAssign({ rackId: rack.id, location: loc })}
        className={`block w-full min-w-0 overflow-hidden min-h-[58px] rounded-sm border p-1.5 text-left transition-colors ${slot ? "border-[#002FA7]/40 bg-[#002FA7]/5 hover:bg-[#002FA7]/10" : "border-dashed border-slate-300 bg-white hover:border-slate-400"}`}
        data-testid={`fg-slot-${loc}`}>
        <div className="truncate text-[8px] font-semibold uppercase tracking-wide text-slate-400">{loc}</div>
        {slot ? (
          <div className="mt-0.5">
            <div className="truncate text-[10px] font-bold leading-tight text-slate-900">{slot.product_name}</div>
            <div className="truncate text-[9px] leading-tight text-slate-500">{dot(slot.design_name, slot.case_pack ? `${slot.case_pack}-Pack` : "")}</div>
            <div className="truncate text-[9px] font-semibold leading-tight text-[#002FA7]">{fgBreakdown(slot.cases, slot.cases_per_pallet, slot.case_pack).join(" · ")}</div>
          </div>
        ) : (
          <div className="mt-1 text-[9px] text-slate-300">Empty</div>
        )}
      </button>
    );
  };

  return (
    <div className="space-y-5" data-testid="fg-shelf-view">
      <div className="rounded-sm border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">Unassigned Finished Goods</span>
        </div>
        <div className="flex gap-2 overflow-x-auto p-3" data-testid="fg-unassigned-list">
          {unassignedList.length === 0 ? (
            <p className="w-full py-4 text-center text-xs text-slate-400">Everything on hand is assigned to a shelf.</p>
          ) : unassignedList.map((f) => (
            <div key={f.id} className="w-[220px] shrink-0 rounded-sm border border-slate-200 p-2.5" data-testid={`fg-unassigned-${f.id}`}>
              <p className="truncate text-sm font-semibold text-slate-900">{f.product_name}</p>
              <p className="truncate text-[11px] text-slate-500">{dot(f.design_name, `${f.case_pack}-Pack`, f.sku)}</p>
              <p className="mt-1 text-xs text-slate-600">{fgBreakdown(f.unassigned, f.cases_per_pallet, f.case_pack).join(" · ")}</p>
              <div className="mt-2 flex gap-1.5">
                <Button size="sm" className="h-7 flex-1 rounded-sm bg-[#002FA7] text-xs hover:bg-[#00218a]"
                  onClick={() => autoAssign(f.id)} data-testid={`fg-auto-assign-${f.id}`}>
                  Auto-assign
                </Button>
                <Button size="sm" variant="outline" className="h-7 flex-1 rounded-sm text-xs"
                  onClick={() => openAssign({ preselect: f.id })} data-testid={`fg-assign-from-unassigned-${f.id}`}>
                  Pick slot
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {needsReconcile && (
        <div className="space-y-2 rounded-sm border border-red-200 bg-red-50 p-3" data-testid="fg-reconcile-warning">
          <div className="flex items-center gap-2 text-xs text-red-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <div className="flex-1 font-semibold">Shelf assignments don't match Finished Goods on hand. Reconcile removes the problems shown below.</div>
            <Button size="sm" variant="outline" disabled={reconciling} className="h-7 shrink-0 rounded-sm border-red-300 text-red-700 hover:bg-red-100" onClick={reconcileNow} data-testid="fg-reconcile-button">
              {reconciling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Reconcile now"}
            </Button>
          </div>
          {excessList.length > 0 && (
            <div className="flex gap-2 overflow-x-auto" data-testid="fg-excess-list">
              {excessList.map((f) => (
                <div key={f.id} className="w-[220px] shrink-0 rounded-sm border border-red-200 bg-white p-2.5" data-testid={`fg-excess-${f.id}`}>
                  <p className="truncate text-sm font-semibold text-slate-900">{f.product_name}</p>
                  <p className="truncate text-[11px] text-slate-500">{dot(f.design_name, `${f.case_pack}-Pack`, f.sku)}</p>
                  <p className="mt-1 text-[11px] text-slate-600">On shelves {fmt(f.assigned)} · On hand {fmt(f.qty_on_hand || 0)} cases</p>
                  <p className="text-xs font-semibold text-red-700">Excess: {fgBreakdown(f.assigned - (f.qty_on_hand || 0), f.cases_per_pallet, f.case_pack).join(" · ")}</p>
                </div>
              ))}
            </div>
          )}
          {orphanSlots.length > 0 && (
            <div className="flex gap-2 overflow-x-auto" data-testid="fg-orphan-list">
              {orphanSlots.map((s) => (
                <div key={s.loc} className="w-[220px] shrink-0 rounded-sm border border-red-200 bg-white p-2.5" data-testid={`fg-orphan-${s.loc}`}>
                  <p className="truncate text-sm font-semibold text-slate-900">{s.name}</p>
                  <p className="truncate text-[11px] text-slate-500">Slot {s.loc} · {fmt(s.cases)} cases</p>
                  <p className="text-xs font-semibold text-red-700">References a deleted item</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-end">
        <Button variant="outline" size="sm" className="h-8 rounded-sm text-xs" onClick={addRack} data-testid="fg-add-rack">
          <Plus className="mr-1 h-3.5 w-3.5" /> Add Rack
        </Button>
      </div>

      {racks.length === 0 ? (
        <p className="border border-dashed border-slate-300 py-16 text-center text-sm text-slate-400" data-testid="fg-no-racks">
          No finished-goods racks yet. Add a rack to start placing pallets.
        </p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {racks.map((rack) => (
            <div key={rack.id} className="rounded-sm border border-slate-200" data-testid={`fg-rack-${rack.code}`}>
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
                <span className="text-sm font-semibold text-slate-800">{rack.name}</span>
                <span className="flex items-center gap-1">
                  <button onClick={() => setRenaming({ id: rack.id, name: rack.name })} className="p-1 text-slate-400 hover:text-slate-700" data-testid={`fg-rename-rack-${rack.code}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => deleteRack(rack)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`fg-delete-rack-${rack.code}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </span>
              </div>
              <div className="space-y-2 p-2">
                {LEVELS.map((level) => (
                  <div key={level}>
                    <div className="mb-1 rounded-sm bg-slate-900 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">Level {level}</div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {ROWS.map((row) => (
                        <div key={row} className="space-y-1">
                          {POS.map((p) => renderSlot(rack, locId(rack.code, level, row, p)))}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent className="rounded-sm sm:max-w-sm" data-testid="fg-rename-dialog">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">Rename rack</DialogTitle></DialogHeader>
          <Input value={renaming?.name || ""} onChange={(e) => setRenaming((p) => ({ ...p, name: e.target.value }))} data-testid="fg-rename-input" />
          <DialogFooter>
            <Button onClick={saveRename} className="rounded-sm bg-[#002FA7] hover:bg-[#00218a]" data-testid="fg-rename-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignAt} onOpenChange={(o) => !o && setAssignAt(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="fg-assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Assign finished goods</DialogTitle>
            <DialogDescription className="text-left">
              {assignAt?.location ? <>Slot <span className="font-mono font-semibold text-[#002FA7]">{assignAt.location}</span></> : "Pick an open slot on a rack, then assign."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={assignFgId || undefined} onValueChange={pickFg}>
              <SelectTrigger className="rounded-sm" data-testid="fg-assign-select">
                <span className={selectedFg ? "" : "text-slate-400"}>{selectedFg ? fgLabel(selectedFg) : "Finished good"}</span>
              </SelectTrigger>
              <SelectContent>
                {fgs.map((f) => (<SelectItem key={f.id} value={f.id}>{f.sku} · {fgLabel(f)}</SelectItem>))}
              </SelectContent>
            </Select>
            {selectedFg && (
              <p className="text-xs text-slate-500" data-testid="fg-assign-available">
                Unassigned: {fgBreakdown(Math.max(0, selectedUnassigned), selectedFg.cases_per_pallet, selectedFg.case_pack).join(" · ")}
                {selectedFg.cases_per_pallet ? ` · ${fmt(selectedFg.cases_per_pallet)} cases per pallet` : " · no pallet size on packing config"}
              </p>
            )}
            <div className="flex gap-2">
              <Input type="text" inputMode="decimal" value={assignQty} onChange={(e) => setAssignQty(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Qty" className="rounded-sm" data-testid="fg-assign-qty" />
              <Select value={assignUnit} onValueChange={setAssignUnit}>
                <SelectTrigger className="w-32 rounded-sm capitalize" data-testid="fg-assign-unit"><span>{assignUnit}</span></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pallets" disabled={!!selectedFg && !selectedFg.cases_per_pallet}>Pallets</SelectItem>
                  <SelectItem value="cases">Cases</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!assignAt?.rackId && <p className="text-[11px] text-amber-600">Close this dialog and click an empty slot on a rack to choose the location.</p>}
          </div>
          <DialogFooter>
            <Button onClick={submitAssign} disabled={!assignAt?.rackId} className="rounded-sm bg-[#002FA7] hover:bg-[#00218a]" data-testid="fg-assign-save">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailAt} onOpenChange={(o) => !o && setDetailAt(null)}>
        <DialogContent className="rounded-sm sm:max-w-sm" data-testid="fg-slot-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">{detailSlot?.product_name}</DialogTitle>
            <DialogDescription className="text-left">
              {dot(detailSlot?.design_name, detailSlot?.case_pack ? `${detailSlot.case_pack}-Pack` : "", detailSlot?.sku)}
              <br />Slot <span className="font-mono font-semibold text-[#002FA7]">{detailAt?.location}</span> · {detailSlot ? fgBreakdown(detailSlot.cases, detailSlot.cases_per_pallet, detailSlot.case_pack).join(" · ") : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm border-red-300 text-red-600 hover:bg-red-50" onClick={() => clearSlot(detailAt.rackId, detailAt.location)} data-testid="fg-clear-slot">
              Clear slot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
